import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

const REFRESH_MS = 45000;

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("ar", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return iso;
  }
};

const StatusPill = ({ ok, okText, failText }) => (
  <span className={`pill ${ok ? "pill-ok" : "pill-fail"}`}>
    {ok ? `\u2714 ${okText}` : `\u2716 ${failText}`}
  </span>
);

export default function MonitorDashboard() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [submission, setSubmission] = useState(null);
  const [approval, setApproval] = useState(null);
  const [extPorts, setExtPorts] = useState(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(null);
  const [dnsCelebrate, setDnsCelebrate] = useState(false);
  const prevDnsRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    setChecking(true);
    setError(null);
    try {
      const res = await api.get("/monitor/status", { timeout: 50000 });
      setStatus(res.data);
      const dnsOk = res.data?.dns?.matched === true;
      if (prevDnsRef.current === false && dnsOk) setDnsCelebrate(true);
      prevDnsRef.current = dnsOk;
      const [h, sub, appr, ext] = await Promise.all([
        api.get("/monitor/history?limit=10", { timeout: 15000 }),
        api.get("/monitor/submission", { timeout: 15000 }),
        api.get("/monitor/approval", { timeout: 15000 }),
        api.get("/monitor/external-ports", { timeout: 15000 }),
      ]);
      setHistory(h.data || []);
      setSubmission(sub.data || null);
      setApproval(appr.data || null);
      setExtPorts(ext.data || null);
    } catch (e) {
      if (e?.response?.status !== 401) {
        console.error("monitor fetch error", e);
        setError("تعذر الفحص الآن — ستتم إعادة المحاولة تلقائياً");
      }
    }
    setChecking(false);
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, REFRESH_MS);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const dns = status?.dns;
  const p8080 = status?.ports?.["8080"];
  const p5060 = status?.ports?.["5060"];
  const http = status?.http;
  const ssh = status?.ssh;
  const tls = status?.tls;

  const ext8080 = extPorts?.results?.["8080"];
  const ext5060 = extPorts?.results?.["5060"];
  const intlBlocked5060 = ext5060 && ext5060.open === 0 && ext5060.blocked > 0;

  const IntlLine = ({ ext, testId }) => {
    if (!ext) return null;
    const total = ext.open + ext.blocked;
    const ok = ext.open > 0;
    return (
      <div className={`intl-line ${ok ? "ok" : "fail"}`} data-testid={testId}>
        {ok ? `🌍 دولياً: مفتوح من ${ext.open}/${total} دول` : `🌍 دولياً: محجوب (0/${total} دول)`}
      </div>
    );
  };

  const remaining = [];
  if (status) {
    if (!dns?.matched) remaining.push(dns?.resolved ? "DNS يوجّه لـ IP خاطئ — صحّح سجل A" : "وجّه DNS: ookla.twin-tc.net \u2190 78.155.66.250");
    if (!p5060?.open || intlBlocked5060) remaining.push("المنفذ 5060 محجوب دولياً من مزود الإنترنت — أرسل خطاب الطلب الرسمي (بالأسفل)");
    if (!p8080?.open) remaining.push("المنفذ 8080 مغلق — افحص التوجيه");
    if (!http?.ok) remaining.push("OoklaServer لا يستجيب على HTTP");
    if (ssh?.connected && !ssh?.process_running) remaining.push("العملية متوقفة على السيرفر");
  }

  return (
    <section className="card monitor-card" data-testid="monitor-dashboard">
      <div className="card-head">
        <h2>📡 لوحة مراقبة السيرفر — مباشر</h2>
        <div className="monitor-head-actions">
          {status && <span className="last-check">آخر فحص: {fmtTime(status.timestamp)}</span>}
          <button className="btn btn-ghost btn-sm" onClick={fetchStatus} disabled={checking} data-testid="refresh-monitor-btn">
            {checking ? <span className="spinner" /> : "\uD83D\uDD04"} {checking ? "جاري الفحص..." : "فحص الآن"}
          </button>
        </div>
      </div>

      {dnsCelebrate && (
        <div className="celebrate-banner" data-testid="dns-celebrate">
          🎉 اكتمل توجيه DNS بنجاح! ookla.twin-tc.net يشير الآن لسيرفرك
          <button className="celebrate-close" onClick={() => setDnsCelebrate(false)}>✕</button>
        </div>
      )}

      {error && <div className="monitor-error" data-testid="monitor-error">{error}</div>}

      {approval?.approved ? (
        <div className="submission-banner success" data-testid="approval-banner">
          🎉 <b>وافقت Ookla رسمياً!</b> السيرفر يظهر الآن على Speedtest.net — ID: {approval.server_id}
        </div>
      ) : (
        submission && (
          <div
            className={`submission-banner ${submission.status}`}
            data-testid="submission-banner"
          >
            {submission.status === "success" && <span>🎉 <b>مسجّل لدى Ookla</b> — بانتظار الموافقة (مراقبة تلقائية كل 10 دقائق + تنبيه فوري)</span>}
            {submission.status === "in_progress" && <span>⏳ جاري التسجيل التلقائي لدى Ookla الآن...</span>}
            {submission.status === "failed" && <span>⚠️ فشل التسجيل التلقائي لدى Ookla (محاولة {submission.attempts}/3) — سيعيد المحاولة تلقائياً</span>}
            {submission.status === "waiting_dns" && <span>🤖 التسجيل التلقائي لدى Ookla جاهز — سينفَّذ فور اكتمال توجيه DNS</span>}
          </div>
        )
      )}

      {!status && checking && (
        <div className="monitor-loading" data-testid="monitor-loading">
          <span className="spinner big" /> جاري فحص سيرفرك 78.155.66.250 من الإنترنت الخارجي...
        </div>
      )}

      {status && (
        <>
          <div className={`overall-banner ${status.overall_ready ? "ready" : "pending"}`} data-testid="overall-banner">
            {status.overall_ready ? (
              <span>🎉 <b>السيرفر جاهز 100%!</b> كل الفحوصات ناجحة — سجّله الآن لدى Ookla</span>
            ) : (
              <div>
                <b>⚠️ متبقي {remaining.length} {remaining.length === 1 ? "خطوة" : "خطوات"}:</b>
                <ul>{remaining.map((r, i) => <li key={i}>{r}</li>)}</ul>
              </div>
            )}
          </div>

          <div className="grid monitor-grid" data-testid="monitor-cards">
            <div className={`mon-card ${dns?.matched ? "ok" : "fail"}`} data-testid="dns-card">
              <div className="mon-title">🌐 فاحص DNS</div>
              <div className="mon-target" dir="ltr">ookla.twin-tc.net</div>
              <StatusPill ok={!!dns?.matched} okText="موجّه بنجاح" failText={dns?.resolved ? "IP خاطئ" : "لم يُوجّه بعد"} />
              <div className="mon-detail" dir="ltr">
                {dns?.resolved ? `\u2192 ${dns.ips.join(", ")}` : `المطلوب: ${dns?.expected_ip || "78.155.66.250"}`}
              </div>
            </div>

            <div className={`mon-card ${p8080?.open ? "ok" : "fail"}`} data-testid="port-8080-card">
              <div className="mon-title">🔌 المنفذ 8080</div>
              <div className="mon-target" dir="ltr">TCP — 78.155.66.250</div>
              <StatusPill ok={!!p8080?.open} okText="مفتوح" failText="مغلق" />
              <div className="mon-detail">{p8080?.open ? `زمن الاستجابة: ${p8080.latency_ms}ms` : "غير متاح من الخارج"}</div>
              <IntlLine ext={ext8080} testId="intl-8080" />
            </div>

            <div className={`mon-card ${p5060?.open && !intlBlocked5060 ? "ok" : "warn-isp"}`} data-testid="port-5060-card">
              <div className="mon-title">🔌 المنفذ 5060</div>
              <div className="mon-target" dir="ltr">TCP — 78.155.66.250</div>
              <StatusPill
                ok={!!p5060?.open && !intlBlocked5060}
                okText="مفتوح"
                failText={intlBlocked5060 ? "محجوب دولياً (ISP)" : p5060?.open ? "مفتوح محلياً" : "محجوب من ISP"}
              />
              <div className="mon-detail">
                {intlBlocked5060
                  ? "مفتوح على السيرفر لكن مزود الإنترنت يحجبه دولياً — أرسل الخطاب الرسمي بالأسفل"
                  : p5060?.open ? `زمن الاستجابة: ${p5060.latency_ms}ms` : "الحجب من مزود الإنترنت (منفذ SIP)"}
              </div>
              <IntlLine ext={ext5060} testId="intl-5060" />
            </div>

            <div className={`mon-card ${http?.ok ? "ok" : "fail"}`} data-testid="http-card">
              <div className="mon-title">🛰️ خدمة OoklaServer</div>
              <div className="mon-target" dir="ltr">crossdomain.xml</div>
              <StatusPill ok={!!http?.ok} okText="تستجيب" failText="لا تستجيب" />
              <div className="mon-detail">{http?.ok ? `HTTP ${http.status_code} — ${http.latency_ms}ms` : "فحص من الإنترنت الخارجي"}</div>
            </div>

            <div className={`mon-card ${ssh?.connected ? (ssh?.process_running ? "ok" : "fail") : "warn"}`} data-testid="ssh-card">
              <div className="mon-title">⚙️ العملية (SSH)</div>
              <div className="mon-target" dir="ltr">root@78.155.66.250:222</div>
              {ssh?.connected ? (
                <>
                  <StatusPill ok={!!ssh.process_running} okText="OoklaServer يعمل" failText="متوقف" />
                  <div className="mon-detail">
                    {ssh.watchdog ? "\u2714 مراقب" : "\u2716 مراقب"} · {ssh.autoboot ? "\u2714 إقلاع تلقائي" : "\u2716 إقلاع"} · {ssh.letsencrypt_config ? "\u2714 TLS" : "\u2716 TLS"}
                  </div>
                </>
              ) : (
                <>
                  <span className="pill pill-warn">⚠ SSH غير متاح</span>
                  <div className="mon-detail">الفحوصات الخارجية مستمرة</div>
                </>
              )}
            </div>

            <div className={`mon-card ${tls?.trusted ? "ok" : tls?.active ? "warn" : "fail"}`} data-testid="tls-card">
              <div className="mon-title">🔐 شهادة HTTPS</div>
              <div className="mon-target" dir="ltr">TLS — ookla.twin-tc.net:8080</div>
              {tls?.trusted ? (
                <>
                  <StatusPill ok okText="موثوقة وفعّالة" failText="" />
                  <div className="mon-detail" dir="ltr">{tls.issuer || "Let's Encrypt"}{tls.expires ? ` — ${tls.expires}` : ""}</div>
                </>
              ) : tls?.active ? (
                <>
                  <span className="pill pill-warn">⚠ مؤقتة (Self-Signed)</span>
                  <div className="mon-detail">تُستبدل تلقائياً بشهادة Let's Encrypt بعد موافقة Ookla</div>
                </>
              ) : (
                <>
                  <StatusPill ok={false} okText="" failText="غير نشطة" />
                  <div className="mon-detail">{tls ? "TLS لا يستجيب" : "بانتظار أول فحص"}</div>
                </>
              )}
            </div>
          </div>

          {history.length > 0 && (
            <div className="history-wrap" data-testid="monitor-history">
              <h3 className="history-title">سجل الفحوصات</h3>
              <div className="history-table-scroll">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>الوقت</th>
                      <th>DNS</th>
                      <th>8080</th>
                      <th>5060</th>
                      <th>HTTP</th>
                      <th>العملية</th>
                      <th>جاهز؟</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.id}>
                        <td dir="ltr">{fmtTime(h.timestamp)}</td>
                        <td className={h.dns?.matched ? "c-ok" : "c-fail"}>{h.dns?.matched ? "\u2714" : "\u2716"}</td>
                        <td className={h.ports?.["8080"]?.open ? "c-ok" : "c-fail"}>{h.ports?.["8080"]?.open ? "\u2714" : "\u2716"}</td>
                        <td className={h.ports?.["5060"]?.open ? "c-ok" : "c-fail"}>{h.ports?.["5060"]?.open ? "\u2714" : "\u2716"}</td>
                        <td className={h.http?.ok ? "c-ok" : "c-fail"}>{h.http?.ok ? "\u2714" : "\u2716"}</td>
                        <td className={h.ssh?.process_running ? "c-ok" : "c-fail"}>{h.ssh?.process_running ? "\u2714" : "\u2716"}</td>
                        <td className={h.overall_ready ? "c-ok" : "c-fail"}>{h.overall_ready ? "\u2714" : "\u2716"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
